/**
 * Notifications Redux Slice
 *
 * Manages notification state including:
 * - Notification list with filtering and pagination
 * - Unread counts
 * - User preferences
 * - Push subscription state
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  NotificationsState,
  Notification,
  NotificationCategory,
  NotificationPreference,
  PushSubscription,
  NotificationListResponse,
  UnreadCountResponse,
} from './notificationsTypes';

// ============================================================================
// Initial State
// ============================================================================

const initialState: NotificationsState = {
  // Data
  notifications: [],
  unreadCount: 0,
  unreadByCategory: {},
  preferences: [],
  pushSubscriptions: [],

  // UI State
  isDropdownOpen: false,
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  activeFilter: 'all',

  // Push State
  pushSupported: false,
  pushPermission: 'default',
  pushSubscribed: false,

  // Pagination
  currentPage: 0,
  totalCount: 0,

  // Error State
  error: null,
  lastFetchedAt: null,
};

function isActionableUnread(notification: Notification): boolean {
  return !notification.isRead && notification.behavior !== 'informational';
}

// ============================================================================
// Slice
// ============================================================================

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // ========================================================================
    // Hydration
    // ========================================================================

    /**
     * Hydrate the entire notifications state (e.g., from localStorage)
     */
    hydrateNotificationsState: (_state, action: PayloadAction<Partial<NotificationsState>>) => {
      return { ...initialState, ...action.payload };
    },

    /**
     * Reset notifications state to initial
     */
    resetNotificationsState: () => initialState,

    // ========================================================================
    // Notification List Management
    // ========================================================================

    /**
     * Set notifications (replace all)
     */
    setNotifications: (state, action: PayloadAction<NotificationListResponse>) => {
      state.notifications = action.payload.notifications;
      state.totalCount = action.payload.total;
      state.hasMore = action.payload.hasMore;
      state.currentPage = 0;
      state.lastFetchedAt = new Date().toISOString();
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Append notifications (for infinite scroll)
     */
    appendNotifications: (state, action: PayloadAction<NotificationListResponse>) => {
      const existingIds = new Set(state.notifications.map((n) => n.id));
      const newNotifications = action.payload.notifications.filter(
        (n) => !existingIds.has(n.id)
      );
      state.notifications = [...state.notifications, ...newNotifications];
      state.totalCount = action.payload.total;
      state.hasMore = action.payload.hasMore;
      state.currentPage += 1;
      state.isLoadingMore = false;
    },

    /**
     * Add a single new notification (from realtime subscription)
     */
    addNotification: (state, action: PayloadAction<Notification>) => {
      const exists = state.notifications.some((n) => n.id === action.payload.id);
      if (!exists) {
        // Prepend new notification at the beginning
        state.notifications = [action.payload, ...state.notifications];
        state.totalCount += 1;

        // Only increment unread count for actionable, unread notifications
        if (isActionableUnread(action.payload)) {
          state.unreadCount += 1;
          const category = action.payload.category;
          state.unreadByCategory[category] = (state.unreadByCategory[category] || 0) + 1;
        }
      }
    },

    /**
     * Update a notification (e.g., mark read)
     */
    updateNotification: (state, action: PayloadAction<Partial<Notification> & { id: string }>) => {
      const index = state.notifications.findIndex((n) => n.id === action.payload.id);
      if (index !== -1) {
        const oldNotification = state.notifications[index];
        const wasUnread = !oldNotification.isRead;
        const isNowRead = action.payload.isRead === true;

        // Merge the update
        state.notifications[index] = { ...oldNotification, ...action.payload };

        // Update unread counts if read status changed
        if (wasUnread && isNowRead && oldNotification.behavior !== 'informational') {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          const category = oldNotification.category;
          state.unreadByCategory[category] = Math.max(
            0,
            (state.unreadByCategory[category] || 0) - 1
          );
        }
      }
    },

    /**
     * Remove a notification (e.g., archived)
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex((n) => n.id === action.payload);
      if (index !== -1) {
        const notification = state.notifications[index];

        // Update unread counts if removing unread notification
        if (isActionableUnread(notification)) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          const category = notification.category;
          state.unreadByCategory[category] = Math.max(
            0,
            (state.unreadByCategory[category] || 0) - 1
          );
        }

        state.notifications.splice(index, 1);
        state.totalCount = Math.max(0, state.totalCount - 1);
      }
    },

    /**
     * Mark notification as read
     */
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload);
      if (notification && !notification.isRead) {
        notification.isRead = true;
        notification.readAt = new Date().toISOString();

        if (notification.behavior !== 'informational') {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          const category = notification.category;
          state.unreadByCategory[category] = Math.max(
            0,
            (state.unreadByCategory[category] || 0) - 1
          );
        }
      }
    },

    /**
     * Mark multiple notifications as read
     */
    markMultipleAsRead: (state, action: PayloadAction<string[]>) => {
      const readAt = new Date().toISOString();
      let unreadDecrement = 0;
      const categoryDecrements: Record<string, number> = {};

      for (const id of action.payload) {
        const notification = state.notifications.find((n) => n.id === id);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          notification.readAt = readAt;
          if (notification.behavior !== 'informational') {
            unreadDecrement += 1;
            categoryDecrements[notification.category] =
              (categoryDecrements[notification.category] || 0) + 1;
          }
        }
      }

      state.unreadCount = Math.max(0, state.unreadCount - unreadDecrement);
      for (const [category, decrement] of Object.entries(categoryDecrements)) {
        state.unreadByCategory[category] = Math.max(
          0,
          (state.unreadByCategory[category] || 0) - decrement
        );
      }
    },

    /**
     * Mark all notifications as read
     */
    markAllAsRead: (state) => {
      const readAt = new Date().toISOString();
      for (const notification of state.notifications) {
        if (!notification.isRead) {
          notification.isRead = true;
          notification.readAt = readAt;
        }
      }
      state.unreadCount = 0;
      state.unreadByCategory = {};
    },

    /**
     * Roll back an optimistic mark-as-read that the server refused (extreme
     * module 02 M4/M7, 2026-08-17: with the notifications API down "Mark all
     * as read" cleared the badge for good and showed nothing). Restores the
     * given notifications to unread and the counts to their snapshot.
     */
    restoreUnread: (
      state,
      action: PayloadAction<{
        ids: string[];
        unreadCount: number;
        unreadByCategory: Record<string, number>;
      }>,
    ) => {
      const ids = new Set(action.payload.ids);
      for (const notification of state.notifications) {
        if (ids.has(notification.id)) {
          notification.isRead = false;
          notification.readAt = undefined;
        }
      }
      state.unreadCount = action.payload.unreadCount;
      state.unreadByCategory = { ...action.payload.unreadByCategory };
    },

    /**
     * Archive notification (soft delete)
     */
    archiveNotification: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload);
      if (notification) {
        notification.isArchived = true;

        // Update unread counts if archiving unread
        if (isActionableUnread(notification)) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          const category = notification.category;
          state.unreadByCategory[category] = Math.max(
            0,
            (state.unreadByCategory[category] || 0) - 1
          );
        }

        // Remove from list (since we filter out archived by default)
        state.notifications = state.notifications.filter((n) => n.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      }
    },

    // ========================================================================
    // Unread Counts
    // ========================================================================

    /**
     * Set unread counts
     */
    setUnreadCounts: (state, action: PayloadAction<UnreadCountResponse>) => {
      state.unreadCount = action.payload.total;
      state.unreadByCategory = action.payload.byCategory;
    },

    /**
     * Increment unread count (e.g., new notification received)
     */
    incrementUnreadCount: (state, action: PayloadAction<NotificationCategory>) => {
      state.unreadCount += 1;
      state.unreadByCategory[action.payload] =
        (state.unreadByCategory[action.payload] || 0) + 1;
    },

    // ========================================================================
    // UI State
    // ========================================================================

    /**
     * Toggle dropdown open/closed
     */
    toggleDropdown: (state) => {
      state.isDropdownOpen = !state.isDropdownOpen;
    },

    /**
     * Set dropdown open state
     */
    setDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.isDropdownOpen = action.payload;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set loading more state (for pagination)
     */
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },

    /**
     * Set active filter
     */
    setActiveFilter: (state, action: PayloadAction<NotificationCategory | 'all'>) => {
      state.activeFilter = action.payload;
      // Reset pagination when filter changes
      state.currentPage = 0;
      state.hasMore = true;
    },

    /**
     * Set error state
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isLoadingMore = false;
    },

    // ========================================================================
    // Preferences
    // ========================================================================

    /**
     * Set all preferences
     */
    setPreferences: (state, action: PayloadAction<NotificationPreference[]>) => {
      state.preferences = action.payload;
    },

    /**
     * Update a single preference (supports partial updates)
     */
    updatePreference: (state, action: PayloadAction<{ category: NotificationCategory | '_all'; updates: Partial<Omit<NotificationPreference, 'category'>> }>) => {
      const { category, updates } = action.payload;
      const index = state.preferences.findIndex(
        (p) => p.category === category
      );
      if (index !== -1) {
        state.preferences[index] = { ...state.preferences[index], ...updates };
      }
      // Note: If preference doesn't exist, we don't create it - that should be handled by fetchPreferences
    },

    // ========================================================================
    // Push Subscriptions
    // ========================================================================

    /**
     * Set push support status
     */
    setPushSupported: (state, action: PayloadAction<boolean>) => {
      state.pushSupported = action.payload;
    },

    /**
     * Set push permission status
     */
    setPushPermission: (state, action: PayloadAction<NotificationPermission>) => {
      state.pushPermission = action.payload;
    },

    /**
     * Set push subscription status
     */
    setPushSubscribed: (state, action: PayloadAction<boolean>) => {
      state.pushSubscribed = action.payload;
    },

    /**
     * Set push subscriptions list
     */
    setPushSubscriptions: (state, action: PayloadAction<PushSubscription[]>) => {
      state.pushSubscriptions = action.payload;
      state.pushSubscribed = action.payload.some((s) => s.isActive);
    },

    /**
     * Add a push subscription
     */
    addPushSubscription: (state, action: PayloadAction<PushSubscription>) => {
      const exists = state.pushSubscriptions.some((s) => s.id === action.payload.id);
      if (!exists) {
        state.pushSubscriptions.push(action.payload);
        state.pushSubscribed = true;
      }
    },

    /**
     * Remove a push subscription
     */
    removePushSubscription: (state, action: PayloadAction<string>) => {
      state.pushSubscriptions = state.pushSubscriptions.filter(
        (s) => s.id !== action.payload
      );
      state.pushSubscribed = state.pushSubscriptions.some((s) => s.isActive);
    },
  },
});

// ============================================================================
// Export Actions
// ============================================================================

export const {
  // Hydration
  hydrateNotificationsState,
  resetNotificationsState,

  // Notification List
  setNotifications,
  appendNotifications,
  addNotification,
  updateNotification,
  removeNotification,
  markAsRead,
  markMultipleAsRead,
  markAllAsRead,
  restoreUnread,
  archiveNotification,

  // Unread Counts
  setUnreadCounts,
  incrementUnreadCount,

  // UI State
  toggleDropdown,
  setDropdownOpen,
  setLoading,
  setLoadingMore,
  setActiveFilter,
  setError,

  // Preferences
  setPreferences,
  updatePreference,

  // Push Subscriptions
  setPushSupported,
  setPushPermission,
  setPushSubscribed,
  setPushSubscriptions,
  addPushSubscription,
  removePushSubscription,
} = notificationsSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

import type { RootState } from '../store';

// Core selectors
export const selectNotifications = (state: RootState) => state.notifications.notifications;
export const selectUnreadCount = (state: RootState) => state.notifications.unreadCount;
export const selectUnreadByCategory = (state: RootState) => state.notifications.unreadByCategory;
export const selectPreferences = (state: RootState) => state.notifications.preferences;
export const selectPushSubscriptions = (state: RootState) => state.notifications.pushSubscriptions;

// UI state selectors
export const selectIsDropdownOpen = (state: RootState) => state.notifications.isDropdownOpen;
export const selectIsLoading = (state: RootState) => state.notifications.isLoading;
export const selectIsLoadingMore = (state: RootState) => state.notifications.isLoadingMore;
export const selectHasMore = (state: RootState) => state.notifications.hasMore;
export const selectActiveFilter = (state: RootState) => state.notifications.activeFilter;
export const selectError = (state: RootState) => state.notifications.error;

// Push state selectors
export const selectPushSupported = (state: RootState) => state.notifications.pushSupported;
export const selectPushPermission = (state: RootState) => state.notifications.pushPermission;
export const selectPushSubscribed = (state: RootState) => state.notifications.pushSubscribed;

// Derived selectors
export const selectFilteredNotifications = (state: RootState) => {
  const { notifications, activeFilter } = state.notifications;
  if (activeFilter === 'all') {
    return notifications;
  }
  return notifications.filter((n) => n.category === activeFilter);
};

export const selectUnreadNotifications = (state: RootState) => {
  return state.notifications.notifications.filter((n) => !n.isRead);
};

export const selectActionableUnreadCount = (state: RootState) => {
  return state.notifications.unreadCount;
};

export const selectPreferenceByCategory = (category: string) => (state: RootState) => {
  return state.notifications.preferences.find((p) => p.category === category);
};

// ============================================================================
// Export Reducer
// ============================================================================

/**
 * The rollback snapshot for an optimistic mark-all-read.
 *
 * WHEN this is called is the whole point (extreme module 02 M4/M7,
 * 2026-08-17). Built BEFORE `markAllAsRead()` it captures the unread ids and
 * counts, so a refused request can be rolled back even when the API is
 * completely unreachable. Built AFTER, it captures zeros and an empty id list,
 * and the rollback silently restores nothing — the badge stays cleared and the
 * user never learns the request failed. The ordering used to be guarded by
 * `expect(body.indexOf('const snapshot')).toBeLessThan(body.indexOf('dispatch(markAllAsRead())'))`,
 * which states line order; notificationsSlice.markAllRollback.test.ts now
 * demonstrates the consequence through the real reducer instead.
 */
export function buildUnreadSnapshot(
  notifications: { id: string; isRead: boolean }[],
  unreadCount: number,
  unreadByCategory: Record<string, number>,
): { ids: string[]; unreadCount: number; unreadByCategory: Record<string, number> } {
  return {
    ids: notifications.filter((n) => !n.isRead).map((n) => n.id),
    unreadCount,
    unreadByCategory: { ...unreadByCategory },
  };
}

export default notificationsSlice.reducer;
